import React, { useState, useRef, useEffect } from 'react';
import { BaseModal } from './BaseModal';
import type { BaseModalProps } from './BaseModal';
import { Button } from '../Button';
import { Input } from '../Input';
import { Textarea } from '../Textarea';
import { HiInformationCircle } from 'react-icons/hi2';
import { formatRichText, sanitizeHtml, extractPlainText } from '../../utils/formatter';

export interface PromptModalProps extends Omit<BaseModalProps, 'children'> {
  message: string;
  placeholder?: string;
  defaultValue?: string;
  multiline?: boolean;
  richText?: boolean;
  required?: boolean;
  maxLength?: number;
  confirmText?: string;
  cancelText?: string;
  onConfirm: (value: string) => void;
  onCancel?: () => void;
  loading?: boolean;
  disabled?: boolean;
  // Props para suporte à minimização
  modalId?: string;
  actionIcon?: React.ReactNode;
  toolbarActionId?: string;
}

export const PromptModal: React.FC<PromptModalProps> = ({
  title = 'Entrada necessária',
  message,
  placeholder,
  defaultValue = '',
  multiline = false,
  richText = true, // Default to rich text
  required = false,
  maxLength,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  loading = false,
  disabled = false,
  isOpen,
  onClose,
  // Props para minimização com valores padrão
  modalId = 'prompt-modal',
  actionIcon = <HiInformationCircle className="w-4 h-4" />,
  toolbarActionId = 'prompt',
  ...props
}) => {
  const [value, setValue] = useState(defaultValue);
  const [isDefaultText, setIsDefaultText] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  // Reset value when modal opens
  useEffect(() => {
    if (isOpen) {
      // Sanitizar e formatar o valor padrão se for rich text
      const sanitizedDefaultValue = richText ? formatRichText(defaultValue) : defaultValue;
      setValue(sanitizedDefaultValue);
      setIsDefaultText(sanitizedDefaultValue.length > 0);
    }
  }, [isOpen, defaultValue, richText]);

  const handleTextChange = (newValue: string) => {
    // Sanitizar input para prevenir XSS e conteúdo malicioso
    const sanitizedValue = sanitizeHtml(newValue);
    setValue(sanitizedValue);
    
    // Se o usuário começou a editar, não é mais texto padrão
    if (isDefaultText && sanitizedValue !== defaultValue) {
      setIsDefaultText(false);
    }
  };

  const handleTextFocus = () => {
    // Se ainda é texto padrão, limpar para permitir edição
    if (isDefaultText && defaultValue.length > 0) {
      setValue('');
      setIsDefaultText(false);
    }
  };

  const handleCancel = () => {
    // Reset para valor padrão sanitizado
    const sanitizedDefaultValue = richText ? formatRichText(defaultValue) : defaultValue;
    setValue(sanitizedDefaultValue);
    setIsDefaultText(sanitizedDefaultValue.length > 0);
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  const handleConfirm = () => {
    if (required && !value.trim()) {
      inputRef.current?.focus();
      return;
    }
    
    // Não permitir múltiplos cliques se estiver carregando
    if (loading) {
      return;
    }
    
    // Formatar o valor final antes de confirmar (para rich text)
    const finalValue = richText ? formatRichText(value) : value;
    onConfirm(finalValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleConfirm();
    }
  };

  const isValid = !required || value.trim().length > 0;

  const getIcon = () => {
    return <HiInformationCircle className="w-8 h-8 text-blue-500" />;
  };

  const getDescription = () => {
    return richText 
      ? 'Use o editor de texto para formatar sua resposta conforme necessário.'
      : multiline
        ? 'Digite sua resposta no campo de texto abaixo.'
        : 'Digite uma resposta no campo abaixo.';
  };

  const footerActions = (
    <div className="flex items-center justify-end space-x-3 p-4">
      <Button
        variant="outline"
        size="sm"
        onClick={handleCancel}
        disabled={loading}
        className="min-w-20"
      >
        {cancelText}
      </Button>
      
      <Button
        variant="primary"
        size="sm"
        onClick={handleConfirm}
        disabled={disabled || !isValid}
        isLoading={loading}
        className="min-w-20"
      >
        {confirmText}
      </Button>
    </div>
  );

  // Calcular estatísticas do texto para exibição
  const getTextStats = () => {
    if (richText && isDefaultText) {
      return 'Texto padrão - clique para personalizar';
    }
    
    if (maxLength) {
      const plainTextLength = richText ? extractPlainText(value).length : value.length;
      return `${plainTextLength}/${maxLength}`;
    }
    
    if (value.length > 0) {
      const plainTextLength = richText ? extractPlainText(value).length : value.length;
      return `${plainTextLength} caracteres`;
    }
    
    return '';
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={title}
      description={getDescription()}
      icon={getIcon()}
      useStandardLayout={true}
      size={multiline || richText ? "lg" : "md"}
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
      resizable={multiline || richText}
      retractable={multiline || richText}
      stickyFooter={true}
      footerContent={footerActions}
      // Props para minimização
      modalId={modalId}
      actionIcon={actionIcon}
      toolbarActionId={toolbarActionId}
      {...props}
    >
      <div className="p-4">
        {/* Message */}
        <div className="text-center mb-4">
          <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
            {message}
          </p>
        </div>

        {/* Input Field */}
        <div className="mb-4">
          {richText || multiline ? (
            <Textarea
              variant={richText ? "rich" : "simple"}
              value={value}
              onChange={richText ? handleTextChange : setValue}
              {...(richText && handleTextFocus ? { onFocus: handleTextFocus } : {})}
              {...(placeholder ? { placeholder } : {})}
              disabled={loading || disabled}
              rows={richText ? 8 : 6}
              className="w-full"
              label=""
              description=""
            />
          ) : (
            <Input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              disabled={loading || disabled}
              maxLength={maxLength}
              className="w-full"
              onKeyDown={handleKeyDown}
            />
          )}
          
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
            {getTextStats()}
          </div>
        </div>
      </div>
    </BaseModal>
  );
};